import { RouteFactory } from "@app/routePaths";
import { BasicCardProps } from "@particles/card/utils/types";
import { SanitizedFeedSectionArticle } from "@typings/Cue";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

/**
 * Helper function to get the season based on the date
 * @param date The date to get the season from
 * @returns {string|undefined} The season based on the date
 */
export const getSeasonBasedOnDate = (inputDate: string): string | undefined => {
  const d = dayjs(inputDate);

  const seasonArray = [
    {
      name: "Chinese New Year",
      range: {
        from: dayjs(`January 01, ${d.year()} 00:00:00`),
        to: dayjs(`January 31, ${d.year()} 23:59:59`),
      },
    },
    {
      name: "Spring",
      range: {
        from: dayjs(`April 01, ${d.year()} 00:00:00`),
        to: dayjs(`April 30, ${d.year()} 23:59:59`),
      },
    },
    {
      name: "Summer",
      range: {
        from: dayjs(`July 01, ${d.year()} 00:00:00`),
        to: dayjs(`July 31, ${d.year()} 23:59:59`),
      },
    },
    {
      name: "Autumn",
      range: {
        from: dayjs(`September 01, ${d.year()} 00:00:00`),
        to: dayjs(`September 30, ${d.year()} 23:59:59`),
      },
    },
    {
      name: "Christmas",
      range: {
        from: dayjs(`December 01, ${d.year()} 00:00:00`),
        to: dayjs(`December 31, ${d.year()} 23:59:59`),
      },
    },
  ];

  const season = seasonArray.find(({ range: { from, to } }) => {
    return dayjs(inputDate).isBetween(from, to, null, "[]"); // Brackets [] means from and to dates are inclusive
  });

  if (typeof season === "undefined") return;

  return season.name;
};

/**
 * Helper function to get the article props for the lifestyle page
 * @param articles - The articles to get the props for
 * @returns {BasicCardProps[]} The article props for the lifestyle page
 */
export const getLifestyleArticleProps = (
  articles: SanitizedFeedSectionArticle[],
  isDisplayMeta: boolean = false,
  size?: number
): BasicCardProps[] => {
  const endSlice = size ? size : articles.length;
  const card = articles
    .slice(0, endSlice)
    .map(
      ({
        id,
        title,
        media,
        urlPath,
        kicker,
        tags,
        paywall,
        blurb,
        sections,
      }) => {
        const mainSection = sections?.[0];
        const mainMedia = media[0];
        const imgSrc = mainMedia?.landscape?.url;
        const imgWidth = mainMedia?.landscape?.width;
        const imgHeight = mainMedia?.landscape?.height;
        const props: BasicCardProps = {
          id,
          title: title,
          thumbnail:
            imgSrc && imgWidth && imgHeight
              ? {
                  src: imgSrc,
                  alt: mainMedia.alt || mainMedia.caption || "",
                  width: imgWidth,
                  height: imgHeight,
                }
              : undefined,
          link: urlPath,
          kicker: kicker?.fields?.[0]?.value?.toUpperCase(),
          tag: tags?.[0],
          displayPaywall: paywall?.contentAccess === "1",
          blurb: blurb,
          defaultImage: {
            directoryName: sections?.[0]?.uniqueName || "",
          },
          meta:
            mainSection && isDisplayMeta
              ? {
                  text: mainSection?.name || "",
                  to: RouteFactory.section(
                    mainSection?.uniqueName.replace("_", "/")
                  ),
                }
              : undefined,
        };

        return props;
      }
    );

  return JSON.parse(JSON.stringify(card));
};
